// An editable area the user can use to write a section (called a block) of the lesson.
// Uses a Tiptap rich-text editor when in edit mode.

import React, { useState } from 'react';
import { Box, Card, CardContent, IconButton, Tooltip } from '@mui/material';
import { Edit, Delete, DeleteForever, Cancel } from '@mui/icons-material';

import LessonBlockEditor from './LessonBlockEditor';

import { AUTHORED_CONTENT_CLASS, authoredContentSx } from '@/shared/theme/authored-content';
import { LessonBlock } from '@/shared/types/lesson';

interface LessonBlockProps {
  block: LessonBlock;
  lessonId: string;
  canEdit: boolean;
  onUpdate?: (updatedBlock: LessonBlock) => void;
  onDelete?: () => void;
}

export const LessonBlockComponent: React.FC<LessonBlockProps> = ({
  block,
  canEdit,
  onUpdate,
  onDelete,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const isMobile = false; // for testing

  /* ---- save / cancel from the Tiptap editor ---- */
  const handleSave = (html: string) => {
    onUpdate?.({ ...block, html });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  /* ---- delete confirmation ---- */
  const handleDeleteClick = () => setIsConfirmingDelete(true);
  const handleConfirmDelete = () => {
    setIsConfirmingDelete(false);
    onDelete?.();
  };
  const handleCancelDelete = () => setIsConfirmingDelete(false);

  /* ---- content rendering ---- */
  const renderContent = () => {
    // Edit mode → full Tiptap editor
    if (isEditing) {
      return (
        <LessonBlockEditor
          initialHtml={block.html || ''}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      );
    }

    // Read-only empty state
    if (!block.html) {
      return (
        <Box
          sx={{
            p: 2,
            textAlign: 'center',
            color: 'text.secondary',
            fontStyle: 'italic',
            cursor: canEdit && !isMobile ? 'pointer' : 'default',
          }}
          onClick={canEdit && !isMobile ? () => setIsEditing(true) : undefined}
        >
          {canEdit && !isMobile ? 'Click to add content...' : 'No content available'}
        </Box>
      );
    }

    // Read-only rendered HTML
    return (
      <Box
        sx={{
          p: 2,
          cursor: canEdit && !isMobile ? 'pointer' : 'default',
          [`& .${AUTHORED_CONTENT_CLASS}`]: authoredContentSx,
        }}
        onClick={canEdit && !isMobile ? () => setIsEditing(true) : undefined}
      >
        <div className={AUTHORED_CONTENT_CLASS} dangerouslySetInnerHTML={{ __html: block.html }} />
      </Box>
    );
  };

  return (
    <Card sx={{ mb: 2, position: 'relative' }}>
      {/* Action buttons — only shown when NOT editing (the editor has its own save/cancel) */}
      {canEdit && !isMobile && !isEditing && (
        <Box className="edit-actions" sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
          {isConfirmingDelete ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title="Confirm delete">
                <IconButton size="small" onClick={handleConfirmDelete} color="error">
                  <DeleteForever />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton size="small" onClick={handleCancelDelete}>
                  <Cancel />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton size="small" onClick={() => setIsEditing(true)} color="primary">
                <Edit />
              </IconButton>
              {onDelete && (
                <Tooltip title="Delete block">
                  <IconButton size="small" onClick={handleDeleteClick} color="default">
                    <Delete />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
      )}

      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>{renderContent()}</CardContent>
    </Card>
  );
};
